import AccountCircle from "@mui/icons-material/AccountCircle";
import Add from "@mui/icons-material/Add";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Circle from "@mui/icons-material/Circle";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Edit from "@mui/icons-material/Edit";
import Error from "@mui/icons-material/Error";
import Info from "@mui/icons-material/Info";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGridPremiumProps,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  gridDetailPanelExpandedRowIdsSelector,
  GridRowId,
  GridRowParams,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import {
  ActivityType,
  FacilityPilotProgramReadyStatus,
  FacilityPilotProgramStatus,
} from "generated-graphql/graphql";
import { LinearIssueStatusMap } from "hooks/useLinearIssueStatuses";
import React, { useCallback, useMemo } from "react";
import { DetailPanelContent } from "./DetailPanelContent";
import { PilotWorkflowTagsCell } from "./PilotWorkflowTagsCell";
import {
  getStatusIconWithoutStar,
  getStatusLabel,
  LocalJurisdictionRow,
  StatusFilter,
} from "./pilotProgramUtils";

// Props interface
interface FacilityPilotProgramLocalProps {
  allLocalRows: LocalJurisdictionRow[];
  loading: boolean;
  statusFilter: StatusFilter;
  linearStatusMap: LinearIssueStatusMap;
  onStatusFilterClick: (filter: StatusFilter) => void;
  onAddFacility: (row: LocalJurisdictionRow) => void;
  onEditProgram: (row: LocalJurisdictionRow) => void;
  onOpenTagsDialog: (row: LocalJurisdictionRow) => void;
  onRemoveFacility: (configId: string) => void;
  removeLoading: boolean;
  yearPicker: React.ReactNode;
}

// Expand/Collapse All Header Component
const ExpandCollapseAllHeader = () => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);

  const expandableRowIds = useMemo(() => {
    return Object.keys(rowsLookup).filter((rowId) => {
      const row = rowsLookup[rowId] as LocalJurisdictionRow;
      return row.pilotFacilities && row.pilotFacilities.length > 0;
    });
  }, [rowsLookup]);

  const allExpanded =
    expandableRowIds.length > 0 &&
    expandableRowIds.every((id) => expandedRowIds.has(id));

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      expandedRowIds.forEach((rowId: GridRowId) => {
        apiRef.current.toggleDetailPanel(rowId);
      });
    } else {
      expandableRowIds.forEach((rowId) => {
        if (!expandedRowIds.has(rowId)) {
          apiRef.current.toggleDetailPanel(rowId);
        }
      });
    }
  };

  return (
    <IconButton
      onClick={handleExpandCollapseAll}
      size="small"
      title={allExpanded ? "Collapse all" : "Expand all"}
    >
      {allExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

// Expand/Collapse Cell Component
const ExpandCollapseCell = ({
  row,
  id,
}: {
  row: LocalJurisdictionRow;
  id: GridRowId;
}) => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const isExpanded = expandedRowIds.has(id);
  const hasPilotFacilities =
    row.pilotFacilities && row.pilotFacilities.length > 0;

  if (!hasPilotFacilities) {
    return null;
  }

  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        apiRef.current.toggleDetailPanel(id);
      }}
    >
      {isExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

export function FacilityPilotProgramLocal({
  allLocalRows,
  loading,
  statusFilter,
  linearStatusMap,
  onStatusFilterClick,
  onAddFacility,
  onEditProgram,
  onOpenTagsDialog,
  onRemoveFacility,
  removeLoading,
  yearPicker,
}: FacilityPilotProgramLocalProps) {
  const apiRef = useGridApiRef();

  // Define columns for local jurisdictions view
  const localColumns: GridColDef<LocalJurisdictionRow>[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderHeader: () => <ExpandCollapseAllHeader />,
        renderCell: ({ row, id }) => <ExpandCollapseCell row={row} id={id} />,
      },
      {
        field: "checklist",
        headerName: "Checklist",
        width: 90,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          return (
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="center"
              alignItems="center"
            >
              {/* Left dot: Pilot Status */}
              <Tooltip title={getStatusLabel(row.status)}>
                {row.readyStatus === FacilityPilotProgramReadyStatus.Ready ? (
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: "success.main", opacity: 0.8 }}
                  />
                ) : (
                  getStatusIconWithoutStar(row.status)
                )}
              </Tooltip>

              {/* Middle dot: QA Complete */}
              <Tooltip
                title={row.qaComplete ? "QA is complete" : "QA is not complete"}
              >
                {row.qaComplete ? (
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: "success.main", opacity: 0.8 }}
                  />
                ) : (
                  <Circle
                    fontSize="small"
                    sx={{ color: "text.disabled", opacity: 0.6 }}
                  />
                )}
              </Tooltip>

              {/* Right dot: Documentation Complete */}
              <Tooltip
                title={
                  row.documentationComplete
                    ? "Documentation is complete"
                    : "Documentation is not complete"
                }
              >
                {row.documentationComplete ? (
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: "success.main", opacity: 0.8 }}
                  />
                ) : (
                  <Circle
                    fontSize="small"
                    sx={{ color: "text.disabled", opacity: 0.6 }}
                  />
                )}
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        field: "state",
        headerName: "State",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="bold">
            {row.state}
          </Typography>
        ),
      },
      {
        field: "localJurisdictionName",
        headerName: "Local Jurisdiction",
        width: 210,
      },
      {
        field: "jurisdictionType",
        headerName: "Type",
        width: 90,
        renderCell: ({ row }) => {
          if (!row.jurisdictionType) return "-";
          // Format the enum value nicely
          const typeMap: Record<string, string> = {
            City: "City",
            County: "County",
            Township: "Township",
            FireDepartment: "Fire Dept",
            LEPC: "LEPC",
            Other: "Other",
          };
          return typeMap[row.jurisdictionType] ?? row.jurisdictionType;
        },
      },
      {
        field: "totalReportingFacilities",
        headerName: "Reporting Facilities",
        width: 105,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Reporting Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="body2">{row.totalReportingFacilities}</Typography>
        ),
      },
      {
        field: "verifiedReportCount",
        headerName: "Candidate Facilities",
        width: 90,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Candidate Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Tooltip title="Reports with encampStatus = PROCESSING">
            <Typography variant="body2">{row.verifiedReportCount}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "pilotFacilityCount",
        headerName: "Pilot Facilities",
        width: 80,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Pilot Facilities
          </Typography>
        ),
        renderCell: ({ row }) => {
          const hasStaffActivity = row.pilotFacilities.some(
            (f) => f.currentActivityType === ActivityType.Staff
          );
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {hasStaffActivity && (
                <AccountCircle
                  sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                />
              )}
              <Typography
                variant="body2"
                fontWeight={row.pilotFacilityCount > 0 ? "bold" : "normal"}
              >
                {row.pilotFacilityCount}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "tags",
        headerName: "Pilot Workflow Tags",
        width: 270,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <PilotWorkflowTagsCell
              row={row}
              onOpenTagsDialog={onOpenTagsDialog}
              linearStatusMap={linearStatusMap}
            />
          );
        },
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 150,
        sortable: false,
        cellClassName: "notes-cell",
        renderCell: ({ row }) => (
          <Tooltip title={row.notes || ""}>
            <Box
              sx={{
                width: "100%",
                fontSize: "0.7rem",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
                wordWrap: "break-word",
                lineHeight: 1.2,
              }}
            >
              {row.notes || ""}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const hasNoCandidateReports = (row.verifiedReportCount ?? 0) === 0;
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {hasNoCandidateReports ? (
                <Tooltip title="No candidate facilities">
                  <span>
                    <IconButton
                      size="small"
                      disabled
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                        cursor: "not-allowed",
                        ".MuiDataGrid-row:hover &": {
                          opacity: 1,
                        },
                      }}
                    >
                      <DoNotDisturb />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Add pilot facility">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddFacility(row);
                    }}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      ".MuiDataGrid-row:hover &": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit pilot program">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditProgram(row);
                  }}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.2s",
                    ".MuiDataGrid-row:hover &": {
                      opacity: 1,
                    },
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [onAddFacility, onEditProgram, onOpenTagsDialog, linearStatusMap]
  );

  // Filter local jurisdiction rows by status filter (show all jurisdictions regardless of reporting facilities count)
  const localRows = useMemo(() => {
    let filtered = allLocalRows;

    // Helper to check if a jurisdiction has any checks
    const hasAnyCheck = (r: LocalJurisdictionRow) =>
      r.readyStatus === FacilityPilotProgramReadyStatus.Ready ||
      r.qaComplete ||
      r.documentationComplete;
    
    // Helper to check if all 3 checklist items are complete
    const hasAllChecks = (r: LocalJurisdictionRow) =>
      r.readyStatus === FacilityPilotProgramReadyStatus.Ready &&
      r.qaComplete &&
      r.documentationComplete;

    // Apply status filter if active (no "enabled" filter for local jurisdictions)
    if (statusFilter !== null) {
      if (statusFilter === "started") {
        // Started: Has at least 1 check OR has pilot facilities, but not all 3 checks
        filtered = filtered.filter(
          (row) => (hasAnyCheck(row) || row.pilotFacilityCount > 0) && !hasAllChecks(row)
        );
      } else if (statusFilter === FacilityPilotProgramStatus.Completed) {
        // Completed: All 3 checkmarks are on
        filtered = filtered.filter(hasAllChecks);
      } else if (statusFilter === FacilityPilotProgramStatus.NotStarted) {
        // Not Started: No checks AND no pilot facilities
        filtered = filtered.filter(
          (row) => !hasAnyCheck(row) && row.pilotFacilityCount === 0
        );
      } else if (statusFilter !== "enabled" && statusFilter !== "notEnabled") {
        filtered = filtered.filter((row) => row.status === statusFilter);
      }
    }

    return filtered;
  }, [allLocalRows, statusFilter]);

  // Calculate summary statistics for local jurisdictions (include all jurisdictions)
  const localSummaryStats = useMemo(() => {
    const filteredRows = allLocalRows;

    if (filteredRows.length === 0) {
      return {
        totalJurisdictions: 0,
        jurisdictionsStarted: 0,
        jurisdictionsStartedPercent: 0,
        jurisdictionsCompleted: 0,
        jurisdictionsCompletedPercent: 0,
        jurisdictionsNotStarted: 0,
        jurisdictionsNotStartedPercent: 0,
        totalPilotFacilities: 0,
        totalVerifiedReports: 0,
        pilotFacilitiesWaitingForStaff: 0,
        jurisdictionsCanBeStarted: 0,
      };
    }

    const totalJurisdictions = filteredRows.length;
    
    // Helper to check if a jurisdiction has any checks
    const hasAnyCheck = (r: LocalJurisdictionRow) =>
      r.readyStatus === FacilityPilotProgramReadyStatus.Ready ||
      r.qaComplete ||
      r.documentationComplete;
    
    // Helper to check if all 3 checklist items are complete
    const hasAllChecks = (r: LocalJurisdictionRow) =>
      r.readyStatus === FacilityPilotProgramReadyStatus.Ready &&
      r.qaComplete &&
      r.documentationComplete;
    
    // Completed: All 3 checkmarks are on
    const jurisdictionsCompleted = filteredRows.filter(hasAllChecks).length;
    
    // Not Started: No checks AND no pilot facilities
    const jurisdictionsNotStarted = filteredRows.filter(
      (r) => !hasAnyCheck(r) && r.pilotFacilityCount === 0
    ).length;
    
    // Started: Has at least 1 check OR has pilot facilities, but not all 3 checks
    const jurisdictionsStarted = filteredRows.filter(
      (r) => (hasAnyCheck(r) || r.pilotFacilityCount > 0) && !hasAllChecks(r)
    ).length;
    
    const totalPilotFacilities = filteredRows.reduce(
      (acc, r) => acc + r.pilotFacilityCount,
      0
    );
    const totalVerifiedReports = filteredRows.reduce(
      (acc, r) => acc + r.verifiedReportCount,
      0
    );
    const pilotFacilitiesWaitingForStaff = filteredRows.reduce(
      (acc, r) =>
        acc +
        r.pilotFacilities.filter(
          (f) => f.currentActivityType === ActivityType.Staff
        ).length,
      0
    );
    // Jurisdictions that have candidate facilities but no pilot facilities yet
    const jurisdictionsCanBeStarted = filteredRows.filter(
      (r) => r.verifiedReportCount > 0 && r.pilotFacilityCount === 0
    ).length;

    return {
      totalJurisdictions,
      jurisdictionsStarted,
      jurisdictionsStartedPercent: Math.round(
        (jurisdictionsStarted / totalJurisdictions) * 100
      ),
      jurisdictionsCompleted,
      jurisdictionsCompletedPercent: Math.round(
        (jurisdictionsCompleted / totalJurisdictions) * 100
      ),
      jurisdictionsNotStarted,
      jurisdictionsNotStartedPercent: Math.round(
        (jurisdictionsNotStarted / totalJurisdictions) * 100
      ),
      totalPilotFacilities,
      totalVerifiedReports,
      pilotFacilitiesWaitingForStaff,
      jurisdictionsCanBeStarted,
    };
  }, [allLocalRows]);

  const getDetailPanelHeight = useCallback<
    NonNullable<DataGridPremiumProps["getDetailPanelHeight"]>
  >(() => "auto", []);

  const handleLocalRowClick = useCallback(
    (params: GridRowParams<LocalJurisdictionRow>) => {
      if (params.row.pilotFacilities && params.row.pilotFacilities.length > 0) {
        apiRef.current?.toggleDetailPanel(params.id);
      }
    },
    [apiRef]
  );

  const getLocalDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps<LocalJurisdictionRow>["getDetailPanelContent"]>
  >(
    ({ row }) => {
      if (!row.pilotFacilities || row.pilotFacilities.length === 0) {
        return null;
      }
      return (
        <DetailPanelContent
          pilotFacilities={row.pilotFacilities}
          onRemove={onRemoveFacility}
          removeLoading={removeLoading}
          linearStatusMap={linearStatusMap}
        />
      );
    },
    [onRemoveFacility, removeLoading, linearStatusMap]
  );

  return (
    <>
      {/* Header: Year Picker + Summary Stats */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap={1}
        sx={{ mb: 2 }}
      >
        {yearPicker}
        
        {/* Summary Stats */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="stretch"
          flexWrap="wrap"
          rowGap={1}
        >
        {/* Jurisdictions Completed mini-stat */}
        <Tooltip title="Jurisdictions with all 3 checklist items complete (Ready, QA, Documentation)">
          <Paper
            variant="outlined"
            onClick={() =>
              onStatusFilterClick(FacilityPilotProgramStatus.Completed)
            }
            sx={{
              px: 1.5,
              py: 0.75,
              minWidth: 120,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              cursor: "pointer",
              transition: "all 0.2s",
              borderWidth: 2,
              borderColor:
                statusFilter === FacilityPilotProgramStatus.Completed
                  ? "success.main"
                  : "transparent",
              bgcolor:
                statusFilter === FacilityPilotProgramStatus.Completed
                  ? "action.hover"
                  : "transparent",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CheckCircle
                fontSize="small"
                sx={{ color: "success.light", opacity: 0.8 }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                Completed
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {localSummaryStats.jurisdictionsCompleted}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                / {localSummaryStats.totalJurisdictions}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={localSummaryStats.jurisdictionsCompletedPercent}
              color="success"
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Paper>
        </Tooltip>

        {/* Jurisdictions Started mini-stat */}
        <Tooltip title="Jurisdictions with at least 1 checklist item or pilot facility (but not complete)">
          <Paper
            variant="outlined"
            onClick={() => onStatusFilterClick("started")}
            sx={{
              px: 1.5,
              py: 0.75,
              minWidth: 120,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              cursor: "pointer",
              transition: "all 0.2s",
              borderWidth: 2,
              borderColor:
                statusFilter === "started" ? "info.main" : "transparent",
              bgcolor: statusFilter === "started" ? "action.hover" : "transparent",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Info fontSize="small" sx={{ color: "info.light", opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                Started
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="h6" fontWeight="bold" color="info.main">
                {localSummaryStats.jurisdictionsStarted}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                / {localSummaryStats.totalJurisdictions}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={localSummaryStats.jurisdictionsStartedPercent}
              color="info"
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Paper>
        </Tooltip>

        {/* Jurisdictions Not Started mini-stat */}
        <Tooltip title="Jurisdictions with no checklist items and no pilot facilities">
          <Paper
            variant="outlined"
            onClick={() =>
              onStatusFilterClick(FacilityPilotProgramStatus.NotStarted)
            }
            sx={{
              px: 1.5,
              py: 0.75,
              minWidth: 120,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              cursor: "pointer",
              transition: "all 0.2s",
              borderWidth: 2,
              borderColor:
                statusFilter === FacilityPilotProgramStatus.NotStarted
                  ? "text.disabled"
                  : "transparent",
              bgcolor:
                statusFilter === FacilityPilotProgramStatus.NotStarted
                  ? "action.hover"
                  : "transparent",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Circle
                fontSize="small"
                sx={{ color: "text.disabled", opacity: 0.6 }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                Not Started
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="h6" fontWeight="bold" color="text.secondary">
                {localSummaryStats.jurisdictionsNotStarted}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                / {localSummaryStats.totalJurisdictions}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={localSummaryStats.jurisdictionsNotStartedPercent}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: "action.hover",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "text.disabled",
                },
              }}
            />
          </Paper>
        </Tooltip>

        {/* Totals summary */}
        <Paper
          variant="outlined"
          sx={{
            px: 1.5,
            py: 0.75,
            minWidth: 140,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 0.25,
            bgcolor: "action.hover",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight="medium">
              {localSummaryStats.pilotFacilitiesWaitingForStaff}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              pilot facilities waiting for Staff
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight="medium">
              {localSummaryStats.jurisdictionsCanBeStarted}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              jurisdictions can be started
            </Typography>
          </Stack>
        </Paper>
        </Stack>
      </Stack>

      {/* Data Grid */}
      <Box sx={{ height: "calc(100vh - 320px)", minHeight: 400, width: "100%", mt: 2 }}>
        <DataGrid<LocalJurisdictionRow>
          apiRef={apiRef}
          rows={localRows}
          columns={localColumns}
          loading={loading}
          getRowId={(row) => `${row.state}:${row.localJurisdictionName}`}
          getDetailPanelContent={getLocalDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          onRowClick={handleLocalRowClick}
          disableRowGrouping={false}
          initialState={{
            sorting: {
              sortModel: [
                { field: "state", sort: "asc" },
                { field: "localJurisdictionName", sort: "asc" },
              ],
            },
          }}
          pageSizeOptions={[25, 51, 100]}
          sx={{
            "& .MuiDataGrid-detailPanel": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-row.Mui-expanded": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-cell.notes-cell": {
              whiteSpace: "normal",
              alignItems: "center",
            },
          }}
        />
      </Box>
    </>
  );
}
